'use client';
import Link from 'next/link';

export default function ResponsibleGamingPage() {
    return (
        <div className="min-h-screen bg-[#F8F7F4]">
            <header className="sticky top-0 z-40 backdrop-blur-md bg-[#F8F7F4]/90 border-b border-gray-200/50">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link href="/" className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                            <span className="text-indigo-600 font-black text-lg">O</span>
                        </div>
                        <span className="font-extrabold text-xl tracking-tight text-gray-900">OBVIO</span>
                    </Link>
                    <Link href="/" className="bg-gray-900 text-white px-4 py-2 rounded-full font-semibold hover:bg-gray-800 transition-colors text-sm">
                        ← Home
                    </Link>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-6 py-16">
                <div className="text-center mb-16">
                    <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 tracking-tight">Responsible Gaming</h1>
                    <p className="text-gray-500 text-lg">Your safety and well-being are our top priorities. Play responsibly and keep it fun.</p>
                </div>

                <div className="space-y-12">
                    <section className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm transition-all hover:shadow-md">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Commitment</h2>
                        <p className="text-gray-600 leading-relaxed">
                            At Obvio, we are committed to providing a safe, secure, and enjoyable experience for all our players. While Obvio is a skill-based competition, we recognize that any form of paid-entry game requires discipline and a healthy mindset. We provide tools and resources to help you stay in control.
                        </p>
                    </section>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Protecting Minors</h3>
                            <p className="text-sm text-gray-600 leading-relaxed mb-6">
                                You must be 18 years of age or older to participate in any paid game on Obvio. We use comprehensive age verification systems to ensure all players meet the legal requirements.
                            </p>
                            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 italic text-sm text-gray-500">
                                Parents: We recommend using filtering software like Net Nanny or CyberPatrol to prevent underage access.
                            </div>
                        </div>
                        <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Stay In Control</h3>
                            <ul className="space-y-3 text-sm text-gray-600">
                                <li className="flex gap-3">
                                    <span className="text-indigo-500 font-bold">•</span>
                                    Only play with money you can afford to lose.
                                </li>
                                <li className="flex gap-3">
                                    <span className="text-indigo-500 font-bold">•</span>
                                    Set time and spending limits before you start.
                                </li>
                                <li className="flex gap-3">
                                    <span className="text-indigo-500 font-bold">•</span>
                                    Don’t chase losses.
                                </li>
                                <li className="flex gap-3">
                                    <span className="text-indigo-500 font-bold">•</span>
                                    Balance gaming with other activities.
                                </li>
                            </ul>
                        </div>
                    </div>

                    <section className="bg-indigo-50 rounded-3xl p-8 border border-indigo-100 shadow-sm">
                        <h2 className="text-2xl font-bold text-indigo-900 mb-6">Player Control Tools</h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-indigo-100/50">
                                <h4 className="font-bold text-gray-900 mb-2">Deposit Limits</h4>
                                <p className="text-xs text-gray-500">Set daily, weekly, or monthly limits on how much you can deposit into your Obvio wallet.</p>
                            </div>
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-indigo-100/50">
                                <h4 className="font-bold text-gray-900 mb-2">Self-Exclusion</h4>
                                <p className="text-xs text-gray-500">Take a break by voluntarily excluding yourself from all paid games for 1, 3, or 6 months.</p>
                            </div>
                        </div>
                        <div className="mt-8 text-center text-sm text-indigo-600">
                            To enable these tools, contact <a href="mailto:info@playobvio.app" className="font-bold underline">info@playobvio.app</a>.
                        </div>
                    </section>

                    <section className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">External Support</h2>
                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="text-center group">
                                <h4 className="font-bold text-gray-900 mb-2">GamCare</h4>
                                <p className="text-xs text-gray-500 mb-4">Confidential support and advice.</p>
                                <a href="https://www.gamcare.org.uk" target="_blank" className="bg-gray-100 py-2 px-4 rounded-full text-xs font-bold hover:bg-indigo-100 hover:text-indigo-600 transition-all border border-transparent hover:border-indigo-200 inline-block">Visit site</a>
                            </div>
                            <div className="text-center group">
                                <h4 className="font-bold text-gray-900 mb-2">BeGambleAware</h4>
                                <p className="text-xs text-gray-500 mb-4">Get help with your gaming.</p>
                                <a href="https://www.begambleaware.org" target="_blank" className="bg-gray-100 py-2 px-4 rounded-full text-xs font-bold hover:bg-indigo-100 hover:text-indigo-600 transition-all border border-transparent hover:border-indigo-200 inline-block">Visit site</a>
                            </div>
                            <div className="text-center group">
                                <h4 className="font-bold text-gray-900 mb-2">National Helpline</h4>
                                <p className="text-xs text-gray-500 mb-4">Available 24/7 for support.</p>
                                <span className="font-bold text-gray-900 text-sm">0808 8020 133</span>
                            </div>
                        </div>
                    </section>
                </div>
            </main>

            <footer className="py-12 border-t border-gray-200 text-center">
                <p className="text-gray-400 text-sm">© 2026 OBVIO. All rights reserved.</p>
            </footer>
        </div>
    );
}
